// ======================
// StepperWrapper
// ======================

import {
    alpha,
    Box,
    BoxProps,
    Button,
    ButtonProps,
    Card,
    Fade,
    LinearProgress,
    linearProgressClasses,
    Slide,
    Step,
    StepLabel,
    Stepper,
    Theme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { createStepperStore, StepperContext, StepperProps, useStepperContext } from "./StepperState";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface StepperWrapperProps {
    stepperProps: StepperProps;
    wrapperBoxProps?: Partial<BoxProps>;
    orientation: "horizontal" | "vertical";
    useTransitions?: boolean;
    displayType?: StepperDisplayType;
}

type StepperDisplayType = "full" | "progress" | "none";

// ======================
// DynamicStepper
// ======================

export const DynamicStepper: React.FC<StepperWrapperProps> = (p) => {
    const { displayType, stepperProps, wrapperBoxProps, orientation, useTransitions } = p;
    let store = useRef(createStepperStore(stepperProps)).current;

    useEffect(() => {
        store.setState(stepperProps);
    }, [stepperProps]);

    return (
        <StepperContext.Provider value={store}>
            <Box {...wrapperBoxProps}>
                {orientation === "horizontal" ? <HorizontalStepper displayType={displayType} useTransitions={useTransitions} /> : <VerticalStepper />}
            </Box>
        </StepperContext.Provider>
    );
};

// ======================
// HorizontalStepper
// ======================

interface HorizontalStepperProps {
    useTransitions?: boolean;
    displayType: StepperDisplayType;
}

const HorizontalStepper: React.FC<HorizontalStepperProps> = (p) => {
    const { useTransitions, displayType: displayTypeProp } = p;
    const activeStep = useStepperContext((s) => s.activeStep);
    const stepConfigs = useStepperContext((s) => s.stepConfigs);
    const currentStepConfig = stepConfigs.find((v) => stepConfigs.indexOf(v) === activeStep);
    const totalSteps = stepConfigs.length;

    const displayType = displayTypeProp ?? "full";

    return (
        <Grid container spacing={3}>
            <Grid size={12}>
                <Card
                    elevation={0}
                    sx={{
                        backgroundColor: (t: Theme) => t.palette.cirrus.medium,
                    }}
                >
                    {displayType === "progress" && (
                        <Box p={0}>
                            <LinearProgress
                                sx={{
                                    height: 7,
                                    borderRadius: 5,
                                    [`&.${linearProgressClasses.colorPrimary}`]: {
                                        backgroundColor: (t: Theme) => t.palette.cirrus.light,
                                    },
                                }}
                                variant={"determinate"}
                                value={((activeStep === 0 ? 0 : activeStep + 1) / totalSteps) * 100}
                            />
                        </Box>
                    )}
                </Card>
            </Grid>
            {displayType === "full" && (
                <Grid size={12}>
                    <Card
                        elevation={0}
                        sx={{
                            backgroundColor: (t: Theme) => t.palette.cirrus.light,
                        }}
                    >
                        <Box p={2}>
                            <Stepper activeStep={activeStep} orientation={"horizontal"}>
                                {stepConfigs.map((s, i) => {
                                    return (
                                        <Step key={i}>
                                            <StepLabel
                                                StepIconProps={{
                                                    color: "green",
                                                    sx: {
                                                        "& .MuiStepIcon-root": {
                                                            "&.Mui-completed": {
                                                                color: "red",
                                                            },
                                                        },
                                                    },
                                                }}
                                            >
                                                {s.label}
                                                <br />
                                                {s.getDescription ? s.getDescription() : null}
                                            </StepLabel>
                                        </Step>
                                    );
                                })}
                            </Stepper>
                        </Box>
                    </Card>
                </Grid>
            )}
            <Grid size={12}>
                {useTransitions ? (
                    <Box position="relative" width="100%">
                        {stepConfigs.map((s, i) => {
                            return (
                                <Box
                                    key={i}
                                    sx={{
                                        position: i === activeStep ? "relative" : "absolute",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        width: "100%",
                                    }}
                                >
                                    {s.renderer()}
                                </Box>
                            );
                        })}
                    </Box>
                ) : (
                    currentStepConfig.renderer()
                )}
            </Grid>
        </Grid>
    );
};

// ======================
// SlideInStep
// ======================

interface SlideInStepProps {
    transitionIn: boolean;
    children: React.ReactElement;
}

export const SlideAndFadeInStep: React.FC<SlideInStepProps> = (p) => {
    const { transitionIn, children } = p;
    const containerRef = useRef();
    const slideDirection = useStepperContext((s) => s.slideDirection);
    const enterDirection = useStepperContext((s) => s.enterDirection);

    const direction = transitionIn ? enterDirection : slideDirection;

    return (
        <Box overflow={"hidden"} width={"100%"} ref={containerRef}>
            <Slide
                easing={{
                    enter: "cubic-bezier(0.4, 0, 0.2, 1)",
                    exit: "cubic-bezier(0.4, 0, 0.2, 1)",
                }}
                timeout={{
                    enter: 500,
                    exit: 500,
                }}
                container={containerRef.current}
                direction={direction}
                in={transitionIn}
                mountOnEnter
                unmountOnExit
            >
                <Box>
                    <Fade
                        timeout={{
                            enter: 600,
                            exit: 400,
                        }}
                        in={transitionIn}
                    >
                        {children}
                    </Fade>
                </Box>
            </Slide>
        </Box>
    );
};

// ======================
// VerticalStepper
// ======================

interface VerticalStepperProps {}

export const VerticalStepper: React.FC<VerticalStepperProps> = (p) => {
    const activeStep = useStepperContext((s) => s.activeStep);
    const stepConfigs = useStepperContext((s) => s.stepConfigs);

    const currentStepConfig = stepConfigs.find((v) => stepConfigs.indexOf(v) === activeStep);

    return (
        <Grid container spacing={3} sx={{ height: "100%" }}>
            <Grid size={3}>
                <Card
                    elevation={0}
                    sx={{
                        backgroundColor: (t: Theme) => t.palette.cirrus.main,
                    }}
                >
                    <Box p={2}>
                        <Stepper
                            activeStep={activeStep}
                            orientation={"vertical"}
                            sx={{
                                backgroundColor: (t: Theme) => t.palette.cirrus.main,
                            }}
                        >
                            {stepConfigs.map((s, i) => {
                                return (
                                    <Step key={i}>
                                        <StepLabel
                                            StepIconProps={{
                                                sx: {
                                                    "&.Mui-completed": {
                                                        color: (t: Theme) => alpha(t.palette.primary.main, 0.5),
                                                    },
                                                },
                                            }}
                                        >
                                            {s.label}
                                            <br />
                                            {s.getDescription ? s.getDescription() : null}
                                        </StepLabel>
                                    </Step>
                                );
                            })}
                        </Stepper>
                    </Box>
                </Card>
            </Grid>
            <Grid size={9}>{currentStepConfig.renderer()}</Grid>
        </Grid>
    );
};

// ======================
// StepperNavButtons
// ======================

interface navButtonProps {
    label?: string;
    disabled?: boolean;
    onClick?: Function;
    props?: ButtonProps;
    hide?: boolean;
}
interface StepperNavButtonsProps {
    backButtonProps?: navButtonProps;
    nextButtonProps?: navButtonProps;
}

export const StepperNavButtons: React.FC<StepperNavButtonsProps> = (p) => {
    const { backButtonProps, nextButtonProps } = p;
    const { t } = useTranslation("common");
    const hasPreviousStep = useStepperContext((s) => s.getHasPreviousStep());
    const hasNextStep = useStepperContext((s) => s.getHasNextStep());
    const goBack = useStepperContext((s) => s.goBackOneStep);
    const goForward = useStepperContext((s) => s.goToNextStep);
    const setSlideDirection = useStepperContext((s) => s.setSlideDirection);
    const setEnterDirection = useStepperContext((s) => s.setEnterDirection);
    const [isTransitioning, setIsTransitioning] = useState(false);

    const onClickBack = () => {
        if (isTransitioning) return;
        setIsTransitioning(true);

        // Going back: step 1 exits sliding down (in=false, direction=up), step 0 enters from above (in=true, direction=down)
        setSlideDirection("up");
        setEnterDirection("down");

        if (!!backButtonProps?.onClick) {
            backButtonProps.onClick();
        }

        // Wait for exit animation to complete before changing step
        setTimeout(() => {
            goBack();
            window.scrollTo(0, 0);
            setIsTransitioning(false);
        }, 500);
    };

    const onClickNext = () => {
        if (isTransitioning) return;
        setIsTransitioning(true);

        // Going forward: step 0 exits sliding up (in=false, direction=down), step 1 enters from below (in=true, direction=up)
        setSlideDirection("down");
        setEnterDirection("up");

        if (!!nextButtonProps?.onClick) {
            nextButtonProps.onClick();
        }

        // Wait for exit animation to complete before changing step
        setTimeout(() => {
            if (hasNextStep) {
                goForward();
            }
            window.scrollTo(0, 0);
            setIsTransitioning(false);
        }, 500);
    };

    return (
        <Grid size={12}>
            <Box display={"flex"} justifyContent={"center"}>
                {!backButtonProps?.hide && (
                    <Box pr={1}>
                        <Button
                            variant="outlined"
                            color="neutral"
                            disabled={isTransitioning || (backButtonProps?.disabled !== undefined ? backButtonProps?.disabled : !hasPreviousStep)}
                            onClick={onClickBack}
                            {...backButtonProps?.props}
                        >
                            {backButtonProps?.label || t("actions.back")}
                        </Button>
                    </Box>
                )}

                {!nextButtonProps?.hide && (
                    <Box>
                        <Button
                            variant={"contained"}
                            color={"primary"}
                            disabled={isTransitioning || (nextButtonProps?.disabled !== undefined ? nextButtonProps?.disabled : !hasNextStep)}
                            onClick={onClickNext}
                            {...nextButtonProps?.props}
                        >
                            {nextButtonProps?.label || t("actions.next")}
                        </Button>
                    </Box>
                )}
            </Box>
        </Grid>
    );
};
